import { Button } from "@repo/ui/components/button";
import { Alert, AlertDescription } from "@repo/ui/components/alert";
import { CheckBoxField } from "@repo/ui/components/check-box-field";
import {
  Form,
  FormField,
  FormItem,
  FormMessage,
  useForm,
  useWatch,
  useFormContext
} from "@repo/ui/components/form";
import { useTranslation, Trans } from "react-i18next";
import SelectDropdown from "~/components/select-dropdown";
import { z } from "zod/v4";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextField } from "@repo/ui/components/text-field";
import { NumericFormatField } from "@repo/ui/components/numeric-format-field";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/components/dialog";
import { Info } from "@repo/ui/components/icon";
import { Icon } from "~/components/ui/icon";
import { useEffect, useMemo, useState } from "react";
import { useFetcher } from 'react-router';
import { useDataDropdown } from "~/utils/home";
import { TFunction } from 'i18next';
import { useWards } from '~/hooks/use-wards';

const formSchema = (t: TFunction<"common">) =>
  z.object({
    categoryId: z.string(t("error.required")),
    project: z.string(t("error.required")),
    block: z.string(t("error.required")),
    floorNumber: z.string(t("error.required")),
    apartmentCode: z.string(t("error.required")),
    name: z
      .string(t("error.required"))
      .min(1, t("error.required"))
      .max(100, t("error.max", { max: 100 })),

    phone: z
      .string(t("error.required"))
      .min(1, t("error.required"))
      .max(11, t("error.max", { max: 11 }))
      .regex(/^\d+$/, t("error.invalid_phone")),
    email: z.email(t("error.invalid_email")).optional().or(z.literal("")),
    expectedPrice: z.coerce
      .number(t("error.required"))
      .positive(t("error.positive")),
    // area is a string in the form state; we validate positivity only when provided.
    // Required check is handled manually before opening the dialog to avoid error flashes on reset.
    area: z
      .string()
      .optional()
      .refine((v) => v == null || v === "" || (!Number.isNaN(Number(v)) && Number(v) > 0), t("error.positive")),
    agree: z.boolean().refine((v) => v === true, t("error.must_accept")),
  });

function CategoryField({ items, label }: { items: { label: string; value: string }[]; label: string }) {
  const { control } = useFormContext();
  return (
    <div className="col-span-2 md:col-span-1">
      <FormField
        name="categoryId"
        control={control}
        render={({ field }) => (
          <FormItem>
            <SelectDropdown
              isControlled
              {...field}
              defaultValue={field.value}
              onValueChange={field.onChange}
              items={items}
              className="w-full"
              label={label}
            />
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

const ApartmentFields = () => {
  const { t } = useTranslation("home");
  return (
    <>
      <TextField
        name="project"
        variant={"filled"}
        label={t("search_box.consign_form.placeholder.project")}
      />
      <TextField
        name="block"
        variant={"filled"}
        label={t("search_box.consign_form.placeholder.building")}
      />
      <TextField
        name="floorNumber"
        variant={"filled"}
        label={t("search_box.consign_form.placeholder.floor")}
      />
      <TextField
        variant={"filled"}
        name="apartmentCode"
        label={t("search_box.consign_form.placeholder.unit_code")}
      />
    </>
  );
};

const AddressFields = () => {
  const { t } = useTranslation("home");
  const { control } = useFormContext();
  const provinceId = useWatch({ control, name: 'provinceId' });
  const { options: wardOptions } = useWards(provinceId);
  const { province } = useDataDropdown();
  return (
    <>
      <FormField
        name="provinceId"
        control={control}
        render={({ field }) => (
          <FormItem>
            <SelectDropdown
              isControlled
              {...field}
              defaultValue={field.value}
              onValueChange={field.onChange}
              items={province}
              className="w-full"
              label={t("search_box.find_form.filters.region")}
            />
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name="wardId"
        control={control}
        render={({ field }) => (
          <FormItem>
            <SelectDropdown
              isControlled
              {...field}
              defaultValue={field.value}
              onValueChange={field.onChange}
              items={wardOptions}
              className="w-full"
              label={t("search_box.consign_form.address.ward")}
            />
            <FormMessage />
          </FormItem>
        )}
      />

      <TextField
        name="address"
        variant={"filled"}
        label={t("search_box.consign_form.address.address")}
      />
    </>
  );
};

function FormHouse({ errorMsg }: { errorMsg: string | null }) {
  const { t } = useTranslation("home");
  return (
    <DialogContent className="w-full max-w-[900px] max-h-[95vh] overflow-scroll">
      <DialogHeader>
        <DialogTitle className="text-3xl font-bold font-helve">
          {t("search_box.consign_form.contact_form.title")}
        </DialogTitle>
        <Alert className="border-0 bg-primary-50 text-primary">
          <Info size={20} />
          <AlertDescription className="text-black">
            {t("search_box.consign_form.contact_form.note")}
          </AlertDescription>
        </Alert>
        {errorMsg ? (
          <Alert variant="destructive">
            <AlertDescription>{errorMsg}</AlertDescription>
          </Alert>
        ) : null}
      </DialogHeader>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <TextField
          name="name"
          label={t(
            "search_box.consign_form.contact_form.fields.full_name.label"
          )}
          placeholder={t(
            "search_box.consign_form.contact_form.fields.full_name.placeholder"
          )}
        />
        <TextField name="email" label="Email" placeholder="Email" />
        <TextField
          name="phone"
          label={t("search_box.consign_form.contact_form.fields.phone.label")}
          placeholder={t(
            "search_box.consign_form.contact_form.fields.phone.placeholder"
          )}
        />
        <NumericFormatField
          numericFormatProps={{
            thousandSeparator: true,
          }}
          name="expectedPrice"
          label={t("search_box.consign_form.contact_form.fields.price.label")}
          placeholder={t(
            "search_box.consign_form.contact_form.fields.price.placeholder"
          )}
          inputMode="numeric"
          description={t(
            "search_box.consign_form.contact_form.fields.price.hint"
          )}
        />
      </div>
      <CheckBoxField
        name="agree"
        label={
          <Trans
            ns="home"
            i18nKey={"search_box.consign_form.contact_form.checkbox.policy"}
            components={{
              span: <span className="text-primary" />,
              p: <p className="hover:cursor-pointer" />,
            }}
          />
        }
      />
      <div className="flex gap-2">
        <Icon name="shield" />
        <p className="text-sm">
          {t("search_box.consign_form.contact_form.privacy")}
        </p>
      </div>

      <Button type="submit" form={"consign-house"}>
        {t("search_box.consign_form.contact_form.submit")}
      </Button>
    </DialogContent>
  );
}
// Removed DropdownDynamic; we compute dynamic fields inline based on category
function ConsignHouse() {
  const { category } = useDataDropdown();
  const { t: common } = useTranslation("common");
  const { t } = useTranslation("home");
  const form = useForm<any>({
    resolver: zodResolver(formSchema(common)),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      expectedPrice: "",
      area: "",
      agree: false,
      categoryId: category[0]?.value || '',
      project: "",
      block: "",
      floorNumber: "",
      apartmentCode: "",
      provinceId: "",
      wardId: "",
      address: "",
    },
    mode: "onChange",
  });
  const fetcher = useFetcher();

  const [open, setOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);
  // Determine form mode by category label (Apartment vs Land/House)
  const categoryIdWatch = form.watch('categoryId');
  const selectedCategory = useMemo(() => category.find((c: { value: any; }) => c.value === categoryIdWatch), [category, categoryIdWatch]);
  const isApartment = (selectedCategory?.label || '').toLowerCase().includes('chung cư') || (selectedCategory?.label || '').toLowerCase().includes('apartment');


  const onSubmit = async () => {
    const json = {
      formType: 'consign',
      ...form.getValues(),
    };
    fetcher.submit(json as any, { method: 'post', encType: 'application/json' });


  };
  const handleCloseModal = (value: boolean) => {
    setErrorMsg(null);
    setOpen(value);

    form.reset({
      ...form.getValues(),
      name: "",
      phone: "",
      email: "",
      expectedPrice: "",
      agree: false
    });
  };



  const handleValidateField = () => {
    const values = form.getValues();
    const required = isApartment
      ? ["project", "block", "floorNumber", "apartmentCode", "categoryId", "area"]
      : ["categoryId", "provinceId", "wardId", "address", "area"];

    // clear old errors
    required.forEach((k) => form.clearErrors(k as any));
    let ok = true;
    required.forEach((k) => {
      const v = (values as any)[k];
      if (v === undefined || v === null || String(v).trim() === "") {
        form.setError(k as any, { type: "required", message: (t as any)("error.required", { ns: "common" }) });
        ok = false;
      }
    });
    if (!ok) return;
    setOpen(true);
  };

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data) {
      if (!fetcher.data.ok) {
        setSuccessMsg(null);
        setErrorMsg("Gửi không thành công. Vui lòng thử lại.");
      } else {
        setErrorMsg(null);
        setSuccessMsg("Đăng ký thành công. Chúng tôi sẽ liên hệ sớm nhất!");
        setOpen(false);
        setSuccessOpen(true);
        // Reset form values when closing success dialog as well for safety
        form.reset();
      }
    }
  }, [fetcher.state, fetcher.data]);
  return (
    <Form {...form}>
      <form
        className="grid grid-cols-2 gap-5 md:grid-cols-3"
        id="consign-house"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <input type="hidden" name="formType" value="consign" />
        <CategoryField
          items={category}
          label={t("search_box.consign_form.real_estate_type")}
        />

        {isApartment ? <ApartmentFields /> : <AddressFields />}
        <div>
          <NumericFormatField
            name="area"
            variant={"filled"}
            label={t("search_box.consign_form.area")}
            // placeholder={t("search_box.consign_form.placeholder.area")}
            numericFormatProps={{ allowNegative: false, decimalScale: 2 }}
          />
        </div>

        <Button
          className="h-12 col-span-2 text-white md:col-span-1 bg-second hover:bg-second/80"
          type="button"
          onClick={handleValidateField}
        >
          {t("search_box.consign_form.submit")}
        </Button>
        <Dialog open={open} onOpenChange={handleCloseModal}>
          <FormHouse errorMsg={errorMsg} />
        </Dialog>
        <Dialog
          open={successOpen}
          onOpenChange={(v) => {
            setSuccessOpen(v);
            if (!v) {
              setSuccessMsg(null);
              form.reset();
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {t("search_box.consign_form.dialog.title")}
              </DialogTitle>
            </DialogHeader>
            <Alert className="border-0 bg-primary-50 text-primary">
              <AlertDescription>
                {successMsg || t("search_box.consign_form.messages.success")}
              </AlertDescription>
            </Alert>
            <div className="flex justify-end">
              <Button onClick={() => setSuccessOpen(false)}>
                {t("search_box.consign_form.actions.close")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </form>
    </Form>
  );
}

export default ConsignHouse;
